import { ButtonHTMLAttributes } from 'react';
import { extendTailwindMerge } from 'tailwind-merge';

const mergeButtonClasses = extendTailwindMerge({
    extend: {
        classGroups: {
            'font-size': [
                'text-label-medium',
                'text-label-medium-light',
                'text-body-large',
                'text-body-medium-bold',
                'text-body-medium',
                'text-body-small',
                'text-body-xsmall',
                'text-heading-small',
                'text-heading-medium',
                'text-heading-large',
            ],
        },
    },
});

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?:
        | 'primary'
        | 'secondary'
        | 'link'
        | 'plain-secondary'
        | 'give'
        | 'secondary-filled';
    loading?: boolean;
};

const LoadingSpinner = () => (
    <span className="absolute right-[10px] inset-y-0 flex items-center">
        <svg
            className="animate-spin"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M28 16C28 18.3734 27.2962 20.6935 25.9776 22.6668C24.6591 24.6402 22.7849 26.1783 20.5922 27.0866C18.3995 27.9948 15.9867 28.2324 13.6589 27.7694C11.3311 27.3064 9.19295 26.1635 7.51472 24.4853C5.83649 22.8071 4.6936 20.6689 4.23058 18.3411C3.76755 16.0133 4.00519 13.6005 4.91345 11.4078C5.8217 9.21509 7.35977 7.34094 9.33316 6.02236C11.3065 4.70379 13.6266 4 16 4"
                stroke="white"
                strokeWidth="3"
            />
        </svg>
    </span>
);

const PrimaryButton: React.FC<Omit<ButtonProps, 'variant'>> = ({
    children,
    disabled,
    loading,
    className,
    ...rest
}) => {
    return (
        <button
            disabled={disabled || loading}
            className={mergeButtonClasses(
                `relative px-6 py-3 rounded-[32px] border-none text-white text-body-medium-bold ${
                    loading
                        ? 'bg-utility-tab-color cursor-not-allowed'
                        : disabled
                          ? 'bg-[#D0CFD9] cursor-not-allowed'
                          : 'bg-guiding-violet hover:bg-guiding-violet-hover active:bg-guiding-violet-active'
                }`,
                className,
            )}
            {...rest}
        >
            {children}
            {loading && <LoadingSpinner />}
        </button>
    );
};

const SecondaryButton: React.FC<Omit<ButtonProps, 'variant'>> = ({
    children,
    disabled,
    loading,
    className,
    ...rest
}) => {
    return (
        <button
            disabled={disabled || loading}
            className={mergeButtonClasses(
                `relative px-6 py-3 rounded-[32px] text-body-medium-bold ${
                    loading
                        ? 'bg-utility-tab-color text-white border-none cursor-not-allowed'
                        : disabled
                          ? 'bg-[#D0CFD9] text-white border-none cursor-not-allowed'
                          : 'border-solid bg-white text-guiding-violet hover:text-guiding-violet-hover active:text-guiding-violet-active border-guiding-violet hover:border-guiding-violet-hover active:border-guiding-violet-active'
                }`,
                className,
            )}
            {...rest}
        >
            {children}
            {loading && <LoadingSpinner />}
        </button>
    );
};

const SecondaryFilledButton: React.FC<Omit<ButtonProps, 'variant'>> = ({
    children,
    disabled,
    loading,
    className,
    ...rest
}) => {
    return (
        <button
            disabled={disabled || loading}
            className={mergeButtonClasses(
                `relative px-2 py-0.5 rounded-[16px] border-none text-guiding-violet text-body-xsmall ${
                    loading
                        ? 'bg-utility-tab-color cursor-not-allowed'
                        : disabled
                          ? 'bg-[#D0CFD9] cursor-not-allowed'
                          : 'bg-utility-guiding-violet-light hover:bg-utility-guiding-violet-dark active:bg-utility-guiding-violet-dark'
                }`,
                className,
            )}
            {...rest}
        >
            {children}
            {loading && <LoadingSpinner />}
        </button>
    );
};

const LinkButton: React.FC<Omit<ButtonProps, 'variant'>> = ({
    children,
    disabled,
    loading,
    className,
    ...rest
}) => {
    return (
        <button
            disabled={disabled || loading}
            className={mergeButtonClasses(
                `relative p-0 border-none bg-transparent text-body-medium-bold flex align-middle justify-center ${
                    loading
                        ? 'text-utility-tab-color cursor-not-allowed'
                        : disabled
                          ? 'text-secondary border-none cursor-not-allowed'
                          : 'text-guiding-violet active:text-guiding-violet-active border-guiding-violet hover:border-guiding-violet-hover active:border-guiding-violet-active hover:underline'
                }`,
                className,
            )}
            {...rest}
        >
            {children}
            {loading && <LoadingSpinner />}
        </button>
    );
};

const PlainSecondaryButton: React.FC<Omit<ButtonProps, 'variant'>> = ({
    children,
    disabled,
    loading,
    className,
    ...rest
}) => {
    return (
        <button
            disabled={disabled || loading}
            className={mergeButtonClasses(
                `relative p-1 border-none bg-transparent text-body-medium-bold flex align-middle justify-center gap-2 ${
                    loading
                        ? 'text-utility-tab-color cursor-not-allowed'
                        : disabled
                          ? 'text-secondary border-none cursor-not-allowed'
                          : 'text-secondary hover:text-primary'
                }`,
                className,
            )}
            {...rest}
        >
            {children}
            {loading && <LoadingSpinner />}
        </button>
    );
};

const GiveButton: React.FC<Omit<ButtonProps, 'variant'>> = ({
    children,
    disabled,
    loading,
    className,
    ...rest
}) => {
    return (
        <button
            disabled={disabled || loading}
            className={mergeButtonClasses(
                `relative px-6 py-3 rounded-[32px] border-none text-white text-body-medium-bold ${
                    loading
                        ? 'bg-utility-tab-color cursor-not-allowed'
                        : disabled
                          ? 'bg-[#D0CFD9] cursor-not-allowed'
                          : 'bg-generosity-orange hover:bg-generosity-orange-hover active:bg-generosity-orange-active'
                }`,
                className,
            )}
            {...rest}
        >
            {children}
            {loading && <LoadingSpinner />}
        </button>
    );
};

export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    ...rest
}) => {
    if (variant === 'primary') return <PrimaryButton {...rest} />;
    if (variant === 'link') return <LinkButton {...rest} />;
    if (variant === 'plain-secondary')
        return <PlainSecondaryButton {...rest} />;
    if (variant === 'give') return <GiveButton {...rest} />;
    if (variant === 'secondary-filled')
        return <SecondaryFilledButton {...rest} />;
    return <SecondaryButton {...rest} />;
};
